import React, { memo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Draggable } from 'react-beautiful-dnd';
import { DragHandle, Edit, Palette } from '@material-ui/icons';
import { TextField, Switch, IconButton, Select, MenuItem, Tooltip } from '@material-ui/core';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import styles from './FiltersBoard.module.scss';

function PortalAware({ children }) {
  if (typeof document === 'undefined') return children;
  return ReactDOM.createPortal(children, document.body);
}

PortalAware.propTypes = {
  children: PropTypes.node.isRequired,
};

const FacetRow = memo(({
  dragId,
  facet,
  index,
  onEdit,
  onEditValues,
  onUpdate,
  dragDisabled,
}) => {
  const localizeMessage = useLocalizedMessage();
  const editingEnabled = Boolean(onEdit);
  const displayType = facet.type === 'range' ? 'number' : (facet.type || 'string');
  const typeLabel = localizeMessage(`customization.filters.modal.type.${displayType}`);
  const handleChange = (field) => (event) => {
    onUpdate(facet._cid, {
      [field]: field === 'visible' ? !facet.visible : event.target.value,
    });
  };

  return (
    <Draggable draggableId={dragId} index={index} isDragDisabled={dragDisabled}>
      {(provided, snapshot) => {
        const content = (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={`${styles.row} ${snapshot.isDragging ? styles.rowDragging : ''}`.trim()}
            style={provided.draggableProps.style}
            data-testid={`facet-row-${facet._cid}`}
          >
            <div
              {...provided.dragHandleProps}
              className={`${styles.dragHandle} ${dragDisabled ? styles.dragHandleDisabled : ''}`.trim()}
              aria-label={localizeMessage('customization.filters.actions.dragFacet', { title: facet.title })}
            >
              <DragHandle fontSize="small" />
            </div>

            {editingEnabled ? (
              <TextField
                className={styles.titleInput}
                fullWidth
                value={facet.title || ''}
                onChange={handleChange('title')}
                InputProps={{ disableUnderline: true }}
                inputProps={{
                  'aria-label': localizeMessage('customization.filters.actions.renameFacet', { title: facet.title }),
                }}
              />
            ) : (
              <span className={styles.titleInput}>{facet.title || facet.id}</span>
            )}

            {editingEnabled ? (
              <Select
                className={styles.typeSelect}
                value={displayType}
                onChange={handleChange('type')}
                fullWidth
                disableUnderline
                inputProps={{
                  'aria-label': localizeMessage('customization.filters.actions.changeType', { title: facet.title }),
                }}
              >
                <MenuItem value="string">{localizeMessage('customization.filters.modal.type.string')}</MenuItem>
                <MenuItem value="number">{localizeMessage('customization.filters.modal.type.number')}</MenuItem>
                <MenuItem value="bool">{localizeMessage('customization.filters.modal.type.bool')}</MenuItem>
                <MenuItem value="color">{localizeMessage('customization.filters.modal.type.color')}</MenuItem>
              </Select>
            ) : (
              <span className={styles.typeSelect}>{typeLabel}</span>
            )}

            <div className={styles.orderCell}>
              {facet.orderNatural ? (
                <span className={styles.orderBadge}>
                  {localizeMessage('customization.filters.board.naturalOrder')}
                </span>
              ) : (
                <span className={`${styles.orderBadge} ${styles.manualOrderBadge}`.trim()}>
                  {localizeMessage('customization.filters.board.position', { position: facet.order })}
                </span>
              )}
            </div>

            <div className={styles.visibilityCell}>
              {editingEnabled ? (
                <Switch
                  checked={Boolean(facet.visible)}
                  onChange={handleChange('visible')}
                  color="primary"
                  inputProps={{
                    'aria-label': localizeMessage('customization.filters.actions.toggleVisibility', { title: facet.title }),
                  }}
                />
              ) : (
                <span>{facet.visible ? '✓' : '—'}</span>
              )}
            </div>

            <div className={styles.actionsCell}>
              {facet.source === 'feature' && (facet.type === 'bool' || facet.type === 'color') && onEditValues && (
                <Tooltip title={localizeMessage('customization.filters.valueEditor.editValues')}>
                  <IconButton
                    className={styles.actionButton}
                    onClick={() => onEditValues(facet)}
                    size="small"
                    aria-label={localizeMessage('customization.filters.valueEditor.editValues')}
                  >
                    <Palette fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {onEdit && (
                <Tooltip title={localizeMessage('customization.filters.actions.editFacet')}>
                  <IconButton
                    className={styles.actionButton}
                    onClick={() => onEdit(facet)}
                    size="small"
                    aria-label={localizeMessage('customization.filters.actions.editFacet')}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
        );

        return snapshot.isDragging ? <PortalAware>{content}</PortalAware> : content;
      }}
    </Draggable>
  );
}, (previous, next) => previous.facet === next.facet
    && previous.index === next.index
    && previous.dragDisabled === next.dragDisabled);

FacetRow.propTypes = {
  dragId: PropTypes.string.isRequired,
  facet: PropTypes.shape({
    _cid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    type: PropTypes.string,
    orderNatural: PropTypes.bool,
    order: PropTypes.number,
    visible: PropTypes.bool,
    source: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onEdit: PropTypes.func,
  onEditValues: PropTypes.func,
  onUpdate: PropTypes.func.isRequired,
  dragDisabled: PropTypes.bool.isRequired,
};

FacetRow.defaultProps = {
  onEdit: null,
  onEditValues: null,
};

export default FacetRow;
